/**
 * CreativeSchema.js
 * ═══════════════════════════════════════════════════════════════
 * Schema layer + compiler system for the Blank Canvas template.
 *
 * Pipeline:
 *   raw data → buildXxxSchema() → CreativeSchema (JSON)
 *            → compileCreativeSchemaToHTML() → HTML string
 *            → iframe srcDoc (preview)
 *            → RenderEngine.renderToImage (export)
 *
 * Templates
 * ─────────
 *   DEAL_SIGNAL          — Meta feed deal ad (Blank Canvas default)
 *   WINTER_EDITORIAL     — "Build a Winter Outfit" carousel cover
 *   STREETWEAR_INTEL     — "The State of Streetwear"
 *   MARKET_INTELLIGENCE  — "The State of Fashion Savings"
 *
 * Adding a new template:
 *   1. Add theme id to SCHEMA_THEMES
 *   2. Write a buildXxxSchema() builder
 *   3. Write private compiler helpers prefixed _si* or _mi*
 *   4. Register in the compileCreativeSchemaToHTML() dispatcher
 * ═══════════════════════════════════════════════════════════════
 */

// ─── Schema constants ────────────────────────────────────────────────────────

export const SCHEMA_FORMATS = Object.freeze({
  MOBILE    : '4:5',
  SQUARE    : '1:1',
  WIDESCREEN: '21:9',
  STORY     : '9:16',
});

export const SCHEMA_THEMES = Object.freeze({
  WINTER_EDITORIAL    : 'winter_editorial',    // white editorial cover
  MAGAZINE_COVER      : 'magazine_cover',      // preserved — product magazine cover
  STREETWEAR_INTEL    : 'streetwear_intel',    // preserved — do not remove
  MARKET_INTELLIGENCE : 'market_intelligence', // preserved — do not remove
  DEAL_SIGNAL         : 'deal_signal',         // ← Blank Canvas default — Meta feed deal ad
  PRODUCT_SHOWCASE    : 'product_showcase',
  EDITORIAL_MONO      : 'editorial_mono',
});

export const SCHEMA_EFFECTS = Object.freeze({
  GRAIN_OVERLAY    : 'grain_overlay',
  SCANLINE         : 'scanline',
  RADIAL_GLOW      : 'radial_glow',
  BURST_RAYS       : 'burst_rays',
  EKG_LINE         : 'ekg_line',          // market signal / heartbeat readout
  GRAPH_GRID       : 'graph_grid',        // vertical data-chart grid lines
  CORNER_BRACKETS  : 'corner_brackets',
  DOT_CONSTELLATION: 'dot_constellation',
  GHOST_WATERMARK  : 'ghost_watermark',
  SECTION_RULES    : 'section_rules',
  BLOOMBERG_TICKER : 'bloomberg_ticker',
});

export const SCHEMA_METRIC_STYLES = Object.freeze({
  HERO      : 'hero',
  SUPPORTING: 'supporting',
  TERTIARY  : 'tertiary',
  DATA_ROW  : 'data_row',   // for intelligence-report bar rows
});

export const SCHEMA_COLORS = Object.freeze({
  WHITE: 'white',
  GREEN: 'green',
  MUTED: 'muted',
});

// ─── Shared font URLs ────────────────────────────────────────────────────────

// Used by dark-theme templates (Streetwear Intel, Market Intelligence)
const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Montserrat:wght@300;400;900&family=Roboto+Mono:wght@300;400;700&display=swap';

// Used by Magazine Cover (adds Cormorant Garamond editorial serif)
const FONT_URL_EDITORIAL =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,600;1,300;1,600&family=Montserrat:wght@300;400;900&family=Roboto+Mono:wght@300;400;700&display=swap';

// ─── Shared color resolver ────────────────────────────────────────────────────

function _color(key, accent) {
  return { white: '#ffffff', green: accent, muted: '#2e2e2e' }[key] || '#ffffff';
}

// ════════════════════════════════════════════════════════════════════════════════
//  TEMPLATE: STREETWEAR INTEL
//  "THE STATE OF STREETWEAR"
//  Bloomberg terminal × luxury fashion editorial
// ════════════════════════════════════════════════════════════════════════════════

/**
 * buildStreetswearIntelSchema
 * ───────────────────────────
 * Constructs the default Blank Canvas creative schema.
 * Uses live platform stats for discount / deals metrics; other
 * fields use editorially curated placeholder values that are
 * clearly marked for future dynamic injection.
 */
export function buildStreetswearIntelSchema({ totalSavings, avgSavingsPct, dealsLive }) {
  const avg   = Math.round(avgSavingsPct || 0);
  const deals = dealsLive || 0;
  const saved = Math.round(totalSavings || 0);

  // Normalise bar widths (0–1) against editorial reference maxima
  const avgBar     = Math.min(avg / 80, 1);            // 80% max avg discount
  const dealsBar   = Math.min(deals / 5000, 1);        // 5 000 deals = full bar
  const savingsBar = Math.min(saved / 100000, 1);      // $100 K = full bar

  return {
    template: 'blank_canvas',
    format  : SCHEMA_FORMATS.MOBILE,
    theme   : SCHEMA_THEMES.STREETWEAR_INTEL,

    background: {
      type       : 'svg_intel',
      style      : 'ekg_wave',
      baseColor  : '#0a0a0a',
      accentColor: '#00E676',
    },

    headline: {
      eyebrow: 'THE STATE OF',
      title  : 'STREETWEAR',
      sub    : 'WEEKLY MARKET INTELLIGENCE REPORT',
    },

    // DATA_ROW metrics render as horizontal rows with progress bars.
    // 'bar' is a 0–1 fraction that controls the bar fill width.
    metrics: [
      {
        id   : 'avg_discount',
        label: 'AVG DISCOUNT',
        value: `${avg}%`,
        sub  : 'PLATFORM AVERAGE',
        bar  : avgBar,
        style: SCHEMA_METRIC_STYLES.DATA_ROW,
        color: SCHEMA_COLORS.GREEN,
      },
      {
        id   : 'deals_live',
        label: 'DEALS LIVE',
        value: deals.toLocaleString(),
        sub  : 'ACTIVE NOW',
        bar  : dealsBar,
        style: SCHEMA_METRIC_STYLES.DATA_ROW,
        color: SCHEMA_COLORS.WHITE,
      },
      {
        id   : 'market_savings',
        label: 'MARKET SAVINGS',
        value: saved >= 1000 ? `$${Math.round(saved / 1000)}K` : `$${saved}`,
        sub  : 'TOTAL TRACKED',
        bar  : savingsBar,
        style: SCHEMA_METRIC_STYLES.DATA_ROW,
        color: SCHEMA_COLORS.WHITE,
      },
      {
        id   : 'hot_category',
        // PLACEHOLDER — inject dynamically when category analytics are ready
        label: 'HOT CATEGORY',
        value: 'FOOTWEAR',
        sub  : 'BY DEAL VOLUME',
        bar  : 0.88,
        style: SCHEMA_METRIC_STYLES.DATA_ROW,
        color: SCHEMA_COLORS.GREEN,
      },
    ],

    effects: [
      SCHEMA_EFFECTS.SCANLINE,
      SCHEMA_EFFECTS.GRAIN_OVERLAY,
      SCHEMA_EFFECTS.RADIAL_GLOW,
      SCHEMA_EFFECTS.GRAPH_GRID,
      SCHEMA_EFFECTS.EKG_LINE,
      SCHEMA_EFFECTS.GHOST_WATERMARK,
      SCHEMA_EFFECTS.BLOOMBERG_TICKER,
      SCHEMA_EFFECTS.CORNER_BRACKETS,
    ],

    cta: { text: 'LINK IN BIO' },

    brand: {
      name: 'STOCKROOM',
      tag : 'THE STOCKROOM INTELLIGENCE SYSTEM',
    },
  };
}

// ── SVG environment (Streetwear Intel) ───────────────────────────────────────

function _siDefs(accent) {
  return `
  <defs>
    <!-- Left-leaning ambient glow behind the data section -->
    <radialGradient id="gSI_L" cx="25%" cy="52%" r="55%">
      <stop offset="0%"   stop-color="${accent}" stop-opacity="0.13"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <!-- Lower-right wave glow -->
    <radialGradient id="gSI_R" cx="72%" cy="88%" r="50%">
      <stop offset="0%"   stop-color="${accent}" stop-opacity="0.18"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <!-- EKG spike localised glow -->
    <radialGradient id="gSI_EKG" cx="50%" cy="53%" r="28%">
      <stop offset="0%"   stop-color="${accent}" stop-opacity="0.10"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <!-- Scan-line texture -->
    <pattern id="pSI_scan" x="0" y="0" width="1080" height="4" patternUnits="userSpaceOnUse">
      <line x1="0" y1="0" x2="1080" y2="0" stroke="${accent}" stroke-opacity="0.006" stroke-width="1"/>
    </pattern>
    <!-- Grain texture -->
    <pattern id="pSI_grain" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
      <circle cx="1"   cy="2"   r="0.35" fill="#ffffff" fill-opacity="0.020"/>
      <circle cx="4.5" cy="0.5" r="0.30" fill="#ffffff" fill-opacity="0.014"/>
      <circle cx="7"   cy="5"   r="0.35" fill="#ffffff" fill-opacity="0.016"/>
      <circle cx="2.5" cy="7"   r="0.28" fill="#ffffff" fill-opacity="0.012"/>
      <circle cx="6"   cy="3"   r="0.25" fill="#ffffff" fill-opacity="0.010"/>
    </pattern>
  </defs>`;
}

function _siBackground(base, accent, effects) {
  const parts = [`  <rect width="1080" height="1350" fill="${base}"/>`];

  if (effects.has(SCHEMA_EFFECTS.SCANLINE))
    parts.push(`  <rect width="1080" height="1350" fill="url(#pSI_scan)"/>`);
  if (effects.has(SCHEMA_EFFECTS.GRAIN_OVERLAY))
    parts.push(`  <rect width="1080" height="1350" fill="url(#pSI_grain)"/>`);
  if (effects.has(SCHEMA_EFFECTS.RADIAL_GLOW)) {
    parts.push(`  <ellipse cx="270"  cy="700"  rx="620" ry="520" fill="url(#gSI_L)"/>`);
    parts.push(`  <ellipse cx="780"  cy="1190" rx="520" ry="400" fill="url(#gSI_R)"/>`);
    parts.push(`  <ellipse cx="540"  cy="720"  rx="350" ry="260" fill="url(#gSI_EKG)"/>`);
  }

  return parts.join('\n');
}

function _siGraphGrid(accent) {
  // Faint vertical graph-paper lines — the Bloomberg terminal aesthetic
  const xs = [80, 200, 320, 440, 560, 680, 800, 920, 1000];
  return xs.map((x, i) => {
    const op = (i === 0 || i === xs.length - 1) ? '0.04' : '0.022';
    return `  <line x1="${x}" y1="96" x2="${x}" y2="1268" stroke="${accent}" stroke-opacity="${op}" stroke-width="1"/>`;
  }).join('\n');
}

function _siEKGLine(accent) {
  // Market signal readout — flat baseline with two spike events
  // Primary spike at x≈470–530 (main market event)
  // Secondary blip at x≈180–220 and x≈740–780
  const d = 'M 0,720 L 160,720 L 180,700 L 200,750 L 220,720 ' +
            'L 440,720 ' +
            'L 470,595 L 490,875 L 510,658 L 530,720 ' +
            'L 720,720 ' +
            'L 740,704 L 760,742 L 780,720 ' +
            'L 1080,720';
  return `
  <!-- EKG core line -->
  <path d="${d}" stroke="${accent}" stroke-width="2" fill="none" stroke-opacity="0.52"/>
  <!-- EKG halo (wider, lower opacity — simulates glow in svg/h2c) -->
  <path d="${d}" stroke="${accent}" stroke-width="10" fill="none" stroke-opacity="0.06"/>`;
}

function _siWaves(accent) {
  return `
  <!-- 3-layer sine wave system (bottom visual field) -->
  <path d="M 0,920 C 90,800 180,800 270,920 C 360,1040 450,1040 540,920 C 630,800 720,800 810,920 C 900,1040 990,1040 1080,920"
        stroke="${accent}" stroke-width="2"   fill="none" stroke-opacity="0.18"/>
  <path d="M 0,858 C 90,738 180,738 270,858 C 360,978  450,978  540,858 C 630,738 720,738 810,858 C 900,978  990,978  1080,858"
        stroke="${accent}" stroke-width="3"   fill="none" stroke-opacity="0.38"/>
  <path d="M 0,982 C 90,902 180,902 270,982 C 360,1062 450,1062 540,982 C 630,902 720,902 810,982 C 900,1062 990,1062 1080,982"
        stroke="${accent}" stroke-width="1"   fill="none" stroke-opacity="0.12"/>
  <!-- Filled wave shapes — bottom grounding mass -->
  <path d="M 0,1100 C 200,980 400,1150 650,1000 S 950,860 1080,940 L 1080,1350 L 0,1350 Z"
        fill="${accent}" fill-opacity="0.045"/>
  <path d="M 0,1220 C 300,1140 600,1290 900,1185 L 1080,1350 L 0,1350 Z"
        fill="${accent}" fill-opacity="0.07"/>`;
}

function _siDecorations(accent, brandName) {
  return `
  <!-- Corner brackets (bottom only — top space belongs to headline) -->
  <path d="M 64,1218 L 64,1286 L 132,1286"    stroke="${accent}" stroke-width="3" fill="none"/>
  <path d="M 948,1286 L 1016,1286 L 1016,1218" stroke="${accent}" stroke-width="3" fill="none"/>
  <!-- Ghost watermark in wave field -->
  <text x="540" y="1155" text-anchor="middle"
        font-family="'Bebas Neue','Arial Narrow',sans-serif"
        font-size="128" letter-spacing="6"
        fill="#ffffff" fill-opacity="0.022">STREETWEAR</text>
  <!-- Bloomberg side ticker -->
  <text x="1056" y="520" font-family="'Roboto Mono',monospace" font-size="9"
        fill="${accent}" fill-opacity="0.18" letter-spacing="2"
        transform="rotate(90 1056 520)">MARKET_FEED_LIVE // SS26</text>`;
}

// ── Content layer (Streetwear Intel) ─────────────────────────────────────────

function _siTopBar(brand, accent) {
  return `
  <div style="position:absolute;top:64px;left:80px;right:80px;
              display:flex;justify-content:space-between;align-items:center;">
    <span style="font-family:'Roboto Mono',monospace;font-size:11px;
                 color:${accent};letter-spacing:3px;font-weight:700;
                 text-transform:uppercase;">${brand.name}&nbsp;INTELLIGENCE</span>
    <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                 color:#222;letter-spacing:2px;">SS26&nbsp;//&nbsp;ISSUE&nbsp;01</span>
  </div>
  <div style="position:absolute;top:94px;left:80px;right:80px;
              height:1px;background:#141414;"></div>`;
}

function _siHeadline(headline, accent) {
  return `
  <div style="position:absolute;top:114px;left:80px;right:80px;">
    <div style="font-family:'Roboto Mono',monospace;font-size:12px;
                color:rgba(0,230,118,0.45);letter-spacing:5px;
                text-transform:uppercase;margin-bottom:6px;">
      ${headline.eyebrow}
    </div>
    <div style="font-family:'Bebas Neue','Arial Narrow',Impact,sans-serif;
                font-size:163px;color:#ffffff;
                line-height:0.86;letter-spacing:-3px;">
      ${headline.title}
    </div>
  </div>
  <!-- Divider + subhead below headline -->
  <div style="position:absolute;top:330px;left:80px;right:80px;">
    <div style="height:1px;background:#141414;margin-bottom:10px;"></div>
    <div style="font-family:'Roboto Mono',monospace;font-size:10px;
                color:#242424;letter-spacing:4px;text-align:right;
                text-transform:uppercase;">
      ${headline.sub}
    </div>
  </div>`;
}

function _siMetricsSection(metrics, accent) {
  const rows = metrics
    .filter(m => m.style === SCHEMA_METRIC_STYLES.DATA_ROW)
    .map(m => {
      const barPct = `${Math.round(Math.min(Math.max(m.bar || 0, 0), 1) * 100)}%`;
      const valColor = _color(m.color, accent);
      return `
    <div style="display:flex;justify-content:space-between;align-items:flex-end;
                padding:14px 0 10px;">
      <div style="display:flex;flex-direction:column;gap:3px;">
        <span style="font-family:'Roboto Mono',monospace;font-size:11px;
                     color:#2c2c2c;letter-spacing:3px;text-transform:uppercase;">
          ${m.label}
        </span>
        <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                     color:#1a1a1a;letter-spacing:2px;">
          ${m.sub || ''}
        </span>
      </div>
      <span style="font-family:'Bebas Neue','Arial Narrow',sans-serif;
                   font-size:52px;color:${valColor};
                   line-height:1;letter-spacing:1px;">
        ${m.value}
      </span>
    </div>
    <div style="position:relative;height:2px;background:#121212;overflow:hidden;">
      <div style="position:absolute;top:0;left:0;height:100%;
                  width:${barPct};background:${accent};opacity:0.55;"></div>
    </div>`;
    })
    .join('');

  return `
  <div style="position:absolute;top:368px;left:80px;right:80px;">
    ${rows}
  </div>`;
}

function _siFooter(cta, brand, accent) {
  return `
  <div style="position:absolute;bottom:64px;left:80px;right:80px;">
    <div style="height:1px;background:#141414;margin-bottom:12px;"></div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;">
      <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                   color:#1a1a1a;letter-spacing:2.5px;text-transform:uppercase;">
        ${brand.tag}
      </span>
      <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
        <span style="font-family:'Montserrat','Helvetica Neue',sans-serif;
                     font-weight:900;font-size:14px;color:#ffffff;
                     letter-spacing:7px;text-transform:uppercase;">
          ${cta.text}
        </span>
        <div style="height:2px;background:${accent};width:100%;"></div>
      </div>
    </div>
  </div>`;
}

// ── Main Streetwear Intel compiler ────────────────────────────────────────────

function _compileStreetswearIntel(schema) {
  const accent  = schema.background?.accentColor || '#00E676';
  const base    = schema.background?.baseColor   || '#0a0a0a';
  const effects = new Set(schema.effects || []);
  const brand   = schema.brand   || { name: 'STOCKROOM', tag: 'THE STOCKROOM INTELLIGENCE SYSTEM' };
  const cta     = schema.cta     || { text: 'LINK IN BIO' };
  const headline = schema.headline || { eyebrow: 'THE STATE OF', title: 'STREETWEAR', sub: '' };

  return `<!DOCTYPE html>
<html>
<head>
<link href="${FONT_URL}" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body,html{width:100%;height:100%;overflow:hidden;background:${base};}
.root{width:100%;height:100%;position:relative;overflow:hidden;}
.bg{position:absolute;inset:0;width:100%;height:100%;}
.ui{position:absolute;inset:0;}
</style>
</head>
<body>
<div class="root">

<!-- ── SVG ENVIRONMENT ─────────────────────────────────────────────────── -->
<svg class="bg" viewBox="0 0 1080 1350" preserveAspectRatio="none"
     xmlns="http://www.w3.org/2000/svg">
${_siDefs(accent)}
${_siBackground(base, accent, effects)}
${effects.has(SCHEMA_EFFECTS.GRAPH_GRID)  ? _siGraphGrid(accent)         : ''}
${effects.has(SCHEMA_EFFECTS.EKG_LINE)    ? _siEKGLine(accent)           : ''}
${_siWaves(accent)}
${effects.has(SCHEMA_EFFECTS.CORNER_BRACKETS) || effects.has(SCHEMA_EFFECTS.GHOST_WATERMARK) || effects.has(SCHEMA_EFFECTS.BLOOMBERG_TICKER)
    ? _siDecorations(accent, brand.name)
    : ''}
</svg>

<!-- ── EDITORIAL CONTENT LAYER ────────────────────────────────────────── -->
<div class="ui">
${_siTopBar(brand, accent)}
${_siHeadline(headline, accent)}
${_siMetricsSection(schema.metrics || [], accent)}
${_siFooter(cta, brand, accent)}
</div>

</div>
</body>
</html>`;
}


// ════════════════════════════════════════════════════════════════════════════════
//  TEMPLATE: MARKET INTELLIGENCE (preserved — do not remove)
//  "THE STATE OF FASHION SAVINGS"
// ════════════════════════════════════════════════════════════════════════════════

export function buildMarketShockSchema({ totalSavings, avgSavingsPct, dealsLive }) {
  const savings = Math.round(totalSavings || 0);
  const avg     = Math.round(avgSavingsPct || 0);
  const deals   = (dealsLive || 0).toLocaleString();

  return {
    template: 'blank_canvas',
    format  : SCHEMA_FORMATS.MOBILE,
    theme   : SCHEMA_THEMES.MARKET_INTELLIGENCE,
    background: {
      type: 'svg_wave', style: 'stockroom_green_glow',
      baseColor: '#080808', accentColor: '#00E676',
    },
    headline: { text: 'THE STATE OF FASHION SAVINGS', style: 'editorial_light' },
    metrics: [
      { id: 'total_savings', label: 'TOTAL MARKET SAVINGS', value: `$${savings.toLocaleString()}`,
        style: SCHEMA_METRIC_STYLES.HERO,       color: SCHEMA_COLORS.WHITE },
      { id: 'avg_saving',    label: 'AVG SAVING',           value: `${avg}%`,
        style: SCHEMA_METRIC_STYLES.SUPPORTING, color: SCHEMA_COLORS.GREEN },
      { id: 'deals_live',    label: 'DEALS LIVE',           value: deals,
        style: SCHEMA_METRIC_STYLES.SUPPORTING, color: SCHEMA_COLORS.WHITE },
    ],
    effects: [
      SCHEMA_EFFECTS.GRAIN_OVERLAY, SCHEMA_EFFECTS.SCANLINE, SCHEMA_EFFECTS.RADIAL_GLOW,
      SCHEMA_EFFECTS.BURST_RAYS, SCHEMA_EFFECTS.CORNER_BRACKETS, SCHEMA_EFFECTS.DOT_CONSTELLATION,
      SCHEMA_EFFECTS.GHOST_WATERMARK, SCHEMA_EFFECTS.SECTION_RULES, SCHEMA_EFFECTS.BLOOMBERG_TICKER,
    ],
    cta:   { text: 'LINK IN BIO', style: 'underline_bar' },
    brand: { name: 'STOCKROOM', tag: 'AU\u00a0·\u00a0SS26' },
  };
}

// ── Market Intelligence SVG helpers ──────────────────────────────────────────

function _miDefs(accent) {
  return `
  <defs>
    <radialGradient id="gStage" cx="50%" cy="95%" r="70%">
      <stop offset="0%"   stop-color="${accent}" stop-opacity="0.28"/>
      <stop offset="55%"  stop-color="${accent}" stop-opacity="0.06"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="gLens" cx="50%" cy="52%" r="38%">
      <stop offset="0%"   stop-color="${accent}" stop-opacity="0.09"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="gTopR" cx="90%" cy="8%" r="35%">
      <stop offset="0%"   stop-color="#00ff88" stop-opacity="0.05"/>
      <stop offset="100%" stop-color="#00ff88" stop-opacity="0"/>
    </radialGradient>
    <pattern id="pScan" x="0" y="0" width="1080" height="4" patternUnits="userSpaceOnUse">
      <line x1="0" y1="0" x2="1080" y2="0" stroke="${accent}" stroke-opacity="0.007" stroke-width="1"/>
    </pattern>
    <pattern id="pGrain" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
      <circle cx="1"   cy="2"   r="0.35" fill="#ffffff" fill-opacity="0.022"/>
      <circle cx="4.5" cy="0.5" r="0.30" fill="#ffffff" fill-opacity="0.016"/>
      <circle cx="7"   cy="5"   r="0.35" fill="#ffffff" fill-opacity="0.018"/>
      <circle cx="2.5" cy="7"   r="0.28" fill="#ffffff" fill-opacity="0.014"/>
      <circle cx="6"   cy="3"   r="0.25" fill="#ffffff" fill-opacity="0.012"/>
    </pattern>
  </defs>`;
}

function _miBackground(base, accent, effects) {
  const p = [`  <rect width="1080" height="1350" fill="${base}"/>`];
  if (effects.has(SCHEMA_EFFECTS.SCANLINE))
    p.push(`  <rect width="1080" height="1350" fill="url(#pScan)"/>`);
  if (effects.has(SCHEMA_EFFECTS.GRAIN_OVERLAY))
    p.push(`  <rect width="1080" height="1350" fill="url(#pGrain)"/>`);
  if (effects.has(SCHEMA_EFFECTS.RADIAL_GLOW)) {
    p.push(`  <ellipse cx="540" cy="1350" rx="950" ry="580" fill="url(#gStage)"/>`);
    p.push(`  <ellipse cx="540" cy="700"  rx="440" ry="340" fill="url(#gLens)"/>`);
    p.push(`  <ellipse cx="1080" cy="0"   rx="500" ry="400" fill="url(#gTopR)"/>`);
  }
  return p.join('\n');
}

function _miBurstRays(accent) {
  const rays = [
    ['0','0','0.030','1'], ['100','0','0.035','1'], ['220','0','0.045','1.5'],
    ['330','0','0.060','2'], ['420','0','0.075','2'], ['480','0','0.085','2'],
    ['540','0','0.095','2.5'], ['600','0','0.085','2'], ['660','0','0.075','2'],
    ['750','0','0.060','2'], ['860','0','0.045','1.5'], ['980','0','0.035','1'],
    ['1080','0','0.030','1'], ['-80','500','0.020','1'], ['1160','500','0.020','1'],
  ];
  return rays.map(([x2, y2, op, sw]) =>
    `  <line x1="540" y1="1380" x2="${x2}" y2="${y2}" stroke="${accent}" stroke-opacity="${op}" stroke-width="${sw}"/>`
  ).join('\n');
}

function _miWaveSystem(accent) {
  return `
  <path d="M 0,1120 C 90,950 180,950 270,1120 C 360,1290 450,1290 540,1120 C 630,950 720,950 810,1120 C 900,1290 990,1290 1080,1120"
        stroke="${accent}" stroke-width="1.5" fill="none" stroke-opacity="0.11"/>
  <path d="M 0,1000 C 90,840 180,840 270,1000 C 360,1160 450,1160 540,1000 C 630,840 720,840 810,1000 C 900,1160 990,1160 1080,1000"
        stroke="${accent}" stroke-width="2"   fill="none" stroke-opacity="0.18"/>
  <path d="M 0,880 C 90,720 180,720 270,880 C 360,1040 450,1040 540,880 C 630,720 720,720 810,880 C 900,1040 990,1040 1080,880"
        stroke="${accent}" stroke-width="3.5" fill="none" stroke-opacity="0.42"/>
  <path d="M 0,760 C 90,630 180,630 270,760 C 360,890 450,890 540,760 C 630,630 720,630 810,760 C 900,890 990,890 1080,760"
        stroke="${accent}" stroke-width="1"   fill="none" stroke-opacity="0.12"/>
  <path d="M 0,640 C 90,540 180,540 270,640 C 360,740 450,740 540,640 C 630,540 720,540 810,640 C 900,740 990,740 1080,640"
        stroke="${accent}" stroke-width="0.8" fill="none" stroke-opacity="0.07"/>
  <path d="M 0,1240 C 180,1090 380,1310 620,1140 S 940,970 1080,1060 L 1080,1350 L 0,1350 Z"
        fill="${accent}" fill-opacity="0.045"/>
  <path d="M 0,1300 C 240,1180 500,1360 760,1230 L 1080,1350 L 0,1350 Z"
        fill="${accent}" fill-opacity="0.08"/>`;
}

function _miDecorations(accent, brandName, effects) {
  const parts = [];
  if (effects.has(SCHEMA_EFFECTS.CORNER_BRACKETS)) {
    parts.push(`
  <path d="M 64,132 L 64,64 L 132,64"     stroke="${accent}" stroke-width="3.5" fill="none"/>
  <path d="M 948,64 L 1016,64 L 1016,132"  stroke="${accent}" stroke-width="3.5" fill="none"/>
  <path d="M 64,1218 L 64,1286 L 132,1286"  stroke="${accent}" stroke-width="3.5" fill="none"/>
  <path d="M 948,1286 L 1016,1286 L 1016,1218" stroke="${accent}" stroke-width="3.5" fill="none"/>`);
  }
  if (effects.has(SCHEMA_EFFECTS.SECTION_RULES)) {
    parts.push(`
  <line x1="80"  y1="226" x2="1000" y2="226" stroke="#ffffff" stroke-opacity="0.06" stroke-width="1"/>
  <line x1="80"  y1="272" x2="1000" y2="272" stroke="#ffffff" stroke-opacity="0.06" stroke-width="1"/>
  <line x1="80"  y1="880" x2="1000" y2="880" stroke="#1a1a1a" stroke-width="1"/>`);
  }
  if (effects.has(SCHEMA_EFFECTS.BLOOMBERG_TICKER)) {
    parts.push(`
  <text x="30" y="900" font-family="'Roboto Mono',monospace" font-size="9"
        fill="${accent}" fill-opacity="0.20" letter-spacing="2"
        transform="rotate(-90 30 900)">AU_MKT 0x4F2B // FEED_LIVE</text>
  <text x="1058" y="500" font-family="'Roboto Mono',monospace" font-size="9"
        fill="${accent}" fill-opacity="0.20" letter-spacing="2"
        transform="rotate(90 1058 500)">STOCKROOM_ANALYTICS // SS26</text>`);
  }
  if (effects.has(SCHEMA_EFFECTS.GHOST_WATERMARK)) {
    parts.push(`
  <text x="540" y="1175" text-anchor="middle"
        font-family="'Montserrat','Arial Black',sans-serif"
        font-weight="900" font-size="144" letter-spacing="20"
        fill="#ffffff" fill-opacity="0.025">${brandName}</text>`);
  }
  if (effects.has(SCHEMA_EFFECTS.DOT_CONSTELLATION)) {
    const dots = [
      [174,148,'1.8','0.14'],[310,148,'1.8','0.10'],[446,148,'1.8','0.10'],
      [582,148,'1.8','0.10'],[718,148,'1.8','0.10'],[854,148,'1.8','0.14'],
      [174,298,'1.5','0.07'],[310,298,'1.5','0.07'],[446,298,'1.5','0.07'],
      [582,298,'1.5','0.07'],[718,298,'1.5','0.07'],[854,298,'1.5','0.07'],
    ];
    parts.push(dots.map(([cx,cy,r,op]) =>
      `  <circle cx="${cx}" cy="${cy}" r="${r}" fill="${accent}" fill-opacity="${op}"/>`
    ).join('\n'));
  }
  return parts.join('');
}

// ── Market Intelligence content helpers ───────────────────────────────────────

function _miContent(schema, accent) {
  const brand = schema.brand || { name: 'STOCKROOM', tag: 'AU\u00a0·\u00a0SS26' };
  const cta   = schema.cta   || { text: 'LINK IN BIO' };
  const hl    = schema.headline || { text: 'THE STATE OF FASHION SAVINGS' };
  const hero  = schema.metrics?.find(m => m.style === SCHEMA_METRIC_STYLES.HERO)
    || { label: 'TOTAL MARKET SAVINGS', value: '$0', color: SCHEMA_COLORS.WHITE };
  const sups  = schema.metrics?.filter(m => m.style === SCHEMA_METRIC_STYLES.SUPPORTING) || [];

  const statCols = sups.map((m, i) => {
    const divider = i < sups.length - 1
      ? `<div style="width:1px;background:linear-gradient(to bottom,transparent,#1e1e1e 30%,#1e1e1e 70%,transparent);align-self:stretch;"></div>`
      : '';
    return `
      <div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:0 60px;">
        <span style="font-family:'Bebas Neue','Arial Narrow',sans-serif;
                     font-size:80px;color:${_color(m.color, accent)};line-height:1;letter-spacing:1px;">
          ${m.value}
        </span>
        <span style="font-family:'Roboto Mono',monospace;font-size:10px;
                     color:#2e2e2e;letter-spacing:3.5px;text-transform:uppercase;">
          ${m.label.replace(/ /g, '\u00a0')}
        </span>
      </div>${divider}`;
  }).join('');

  return `
  <div style="position:absolute;top:80px;left:80px;right:80px;
              display:flex;justify-content:space-between;align-items:center;">
    <span style="font-family:'Roboto Mono',monospace;font-size:14px;
                 color:${accent};letter-spacing:4px;font-weight:700;">${brand.name}</span>
    <div style="display:flex;align-items:center;gap:14px;">
      <span style="width:6px;height:6px;border-radius:50%;background:${accent};display:inline-block;"></span>
      <span style="font-family:'Roboto Mono',monospace;font-size:10px;color:#252525;letter-spacing:2px;">LIVE</span>
    </div>
  </div>

  <div style="position:absolute;top:220px;left:80px;right:80px;">
    <div style="font-family:'Montserrat','Helvetica Neue',sans-serif;
                font-weight:300;font-size:13px;color:rgba(0,230,118,0.75);
                letter-spacing:7px;text-transform:uppercase;padding:10px 0;">
      ${hl.text}
    </div>
  </div>

  <div style="position:absolute;top:310px;left:0;right:0;
              display:flex;flex-direction:column;align-items:center;">
    <div style="font-family:'Roboto Mono',monospace;font-size:11px;color:#2a2a2a;
                letter-spacing:5px;text-transform:uppercase;margin-bottom:18px;">
      ${hero.label.replace(/ /g, '\u00a0')}
    </div>
    <div style="font-family:'Bebas Neue','Arial Narrow',Impact,sans-serif;
                font-size:155px;color:${_color(hero.color, accent)};
                line-height:0.90;letter-spacing:-3px;text-align:center;padding:0 40px;">
      ${hero.value}
    </div>
    <div style="display:flex;align-items:center;gap:16px;margin:30px 0 24px;">
      <div style="width:48px;height:2px;background:rgba(0,230,118,0.3);"></div>
      <div style="width:8px;height:8px;border-radius:50%;background:${accent};"></div>
      <div style="width:48px;height:2px;background:rgba(0,230,118,0.3);"></div>
    </div>
    <div style="display:flex;gap:0;align-items:stretch;">${statCols}</div>
  </div>

  <div style="position:absolute;bottom:80px;left:80px;right:80px;
              display:flex;justify-content:space-between;align-items:flex-end;">
    <div style="display:flex;flex-direction:column;gap:9px;">
      <span style="font-family:'Montserrat','Helvetica Neue',sans-serif;
                   font-weight:900;font-size:15px;color:#ffffff;
                   letter-spacing:8px;text-transform:uppercase;">${cta.text}</span>
      <div style="height:2px;background:${accent};width:100%;"></div>
    </div>
    <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                 color:#181818;letter-spacing:2px;">${brand.tag}</span>
  </div>`;
}

function _compileMarketIntelligence(schema) {
  const accent  = schema.background?.accentColor || '#00E676';
  const base    = schema.background?.baseColor   || '#080808';
  const effects = new Set(schema.effects || []);
  const brand   = schema.brand || { name: 'STOCKROOM' };

  return `<!DOCTYPE html>
<html>
<head>
<link href="${FONT_URL}" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body,html{width:100%;height:100%;overflow:hidden;background:${base};}
.root{width:100%;height:100%;position:relative;overflow:hidden;}
.bg{position:absolute;inset:0;width:100%;height:100%;}
.ui{position:absolute;inset:0;}
</style>
</head>
<body>
<div class="root">
<svg class="bg" viewBox="0 0 1080 1350" preserveAspectRatio="none"
     xmlns="http://www.w3.org/2000/svg">
${_miDefs(accent)}
${_miBackground(base, accent, effects)}
${effects.has(SCHEMA_EFFECTS.BURST_RAYS) ? _miBurstRays(accent) : ''}
${_miWaveSystem(accent)}
${_miDecorations(accent, brand.name, effects)}
</svg>
<div class="ui">
${_miContent(schema, accent)}
</div>
</div>
</body>
</html>`;
}


// ════════════════════════════════════════════════════════════════════════════════
//  TEMPLATE: MAGAZINE COVER  (default Blank Canvas)
//  "FASHION MAGAZINE COVER GENERATOR — WHITE EDITION"
//  Vogue × SSENSE × COS — luxury editorial white
// ════════════════════════════════════════════════════════════════════════════════

const _EDITORIAL_HEADLINES = [
  { h1: 'THE NEW',  h2: 'SEASON',   sub: 'Effortless luxury, endlessly refined.' },
  { h1: 'FORM',     h2: 'FOLLOWS',  sub: 'The silhouette the season demands.' },
  { h1: 'THE',      h2: 'EDIT',     sub: 'Curated for those who know.' },
  { h1: 'LESS IS',  h2: 'MORE',     sub: 'Where restraint becomes power.' },
  { h1: 'REFINED',  h2: 'LUXURY',   sub: 'The wardrobe speaks for itself.' },
  { h1: 'A NEW',    h2: 'CHAPTER',  sub: 'The pieces that define how you move.' },
];

/**
 * buildMagazineCoverSchema
 * ────────────────────────
 * Constructs the Fashion Magazine Cover Blank Canvas schema.
 * Selects a deterministic editorial headline from the product name.
 *
 * @param {{ product: object|null, totalSavings: number,
 *            avgSavingsPct: number, dealsLive: number }} data
 */
export function buildMagazineCoverSchema({ product, totalSavings, avgSavingsPct, dealsLive }) {
  const p = product || null;

  // Deterministic headline — same product always gets the same headline
  const nameHash = (p?.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hl = _EDITORIAL_HEADLINES[nameHash % _EDITORIAL_HEADLINES.length];

  // Discount: prefer explicit field, fall back to price comparison
  const discount = p?.discountPercentage
    ? `${Math.round(p.discountPercentage)}%`
    : (p?.originalPrice && p?.price && Number(p.originalPrice) > Number(p.price))
      ? `${Math.round((1 - Number(p.price) / Number(p.originalPrice)) * 100)}%`
      : null;

  const price = p?.price ? `$${parseFloat(p.price).toFixed(2)}` : null;

  return {
    template: 'blank_canvas',
    format  : SCHEMA_FORMATS.MOBILE,
    theme   : SCHEMA_THEMES.MAGAZINE_COVER,

    background: {
      type      : 'editorial_white',
      baseColor : '#ffffff',
      accentColor: '#000000',
    },

    // Product data injected into the cover layout.
    // null = show elegant placeholder (design still looks complete).
    product: p ? {
      imageUrl : p.images?.[0] || null,
      name     : p.name     || 'FEATURED ITEM',
      brand    : (p.brand || 'STOCKROOM').toUpperCase(),
      category : (p.category || 'FASHION').toUpperCase(),
      price,
      discount,
    } : null,

    headline: { h1: hl.h1, h2: hl.h2, sub: hl.sub },
    issue   : { number: 'ISSUE 01', season: 'SPRING / SUMMER 26' },
    effects : [SCHEMA_EFFECTS.GRAIN_OVERLAY],
    cta     : { text: 'LINK IN BIO' },
    brand   : { name: 'STOCKROOM', tag: 'SS26' },
  };
}

// ── Magazine Cover compiler ───────────────────────────────────────────────────

function _compileMagazineCover(schema) {
  const product  = schema.product  || null;
  const headline = schema.headline || { h1: 'THE', h2: 'EDIT', sub: '' };
  const issue    = schema.issue    || { number: 'ISSUE 01', season: 'SS26' };
  const cta      = schema.cta      || { text: 'LINK IN BIO' };
  const brand    = schema.brand    || { name: 'STOCKROOM' };

  const imageUrl  = product?.imageUrl  || null;
  const pBrand    = product?.brand     || brand.name;
  const pCategory = product?.category  || '';
  const pPrice    = product?.price     || null;
  const pDiscount = product?.discount  || null;

  const h1 = (headline.h1 || 'THE NEW').toUpperCase();
  const h2 = (headline.h2 || 'SEASON').toUpperCase();
  const sub = headline.sub || '';

  // Adaptive font size — shorter words get bigger type
  const h1Size = h1.length > 8 ? '62px' : h1.length > 5 ? '72px' : '82px';
  const h2Size = h2.length > 8 ? '62px' : h2.length > 5 ? '72px' : '82px';

  return `<!DOCTYPE html>
<html>
<head>
<link href="${FONT_URL_EDITORIAL}" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body,html{width:100%;height:100%;overflow:hidden;background:#fff;}
.root{width:100%;height:100%;position:relative;overflow:hidden;background:#fff;}
</style>
</head>
<body>
<div class="root">

<!-- ── GRAIN OVERLAY — z:5, pointer-events:none ───────────────────────
     Dark sub-pixel dots on white simulate premium matte print paper.
     Sits above the product image but below the sharp editorial type. -->
<svg style="position:absolute;inset:0;width:100%;height:100%;z-index:5;pointer-events:none;"
     viewBox="0 0 1080 1350" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="mcGrain" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
      <circle cx="1"   cy="2"   r="0.28" fill="#000" fill-opacity="0.013"/>
      <circle cx="4"   cy="5"   r="0.28" fill="#000" fill-opacity="0.010"/>
      <circle cx="3"   cy="0"   r="0.22" fill="#000" fill-opacity="0.009"/>
      <circle cx="5.5" cy="3"   r="0.20" fill="#000" fill-opacity="0.008"/>
    </pattern>
  </defs>
  <rect width="1080" height="1350" fill="url(#mcGrain)"/>
</svg>

<!-- ── MASTHEAD — z:10 ─────────────────────────────────────────────────
     White-space rule + brand + issue info.
     Mirrors how Vogue anchors its masthead above the image. -->
<div style="position:absolute;top:0;left:0;right:0;z-index:10;padding:46px 68px 0;">
  <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:18px;">
    <span style="font-family:'Montserrat','Helvetica Neue',sans-serif;
                 font-size:20px;font-weight:900;letter-spacing:11px;
                 color:#000;text-transform:uppercase;">${brand.name}</span>
    <div style="display:flex;align-items:center;gap:12px;">
      <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                   color:#b0b0b0;letter-spacing:2px;text-transform:uppercase;">
        ${issue.number}
      </span>
      <span style="color:#d0d0d0;font-size:9px;">&middot;</span>
      <span style="font-family:'Roboto Mono',monospace;font-size:9px;
                   color:#b0b0b0;letter-spacing:2px;text-transform:uppercase;">
        ${issue.season}
      </span>
    </div>
  </div>
  <div style="height:1.5px;background:#000;"></div>
</div>

<!-- ── PRODUCT IMAGE — z:1 (behind typography) ─────────────────────────
     The product image acts as an editorial visual element, not a
     product card. Typography layers on top at z:10. -->
<div style="position:absolute;top:86px;left:0;right:0;bottom:358px;z-index:1;
            display:flex;align-items:center;justify-content:center;background:#fff;">
  ${imageUrl ? `
  <img src="${imageUrl}"
       style="max-width:82%;max-height:96%;object-fit:contain;"
       crossorigin="anonymous"
       alt="" />
  ` : `
  <div style="display:flex;flex-direction:column;align-items:center;gap:22px;">
    <div style="width:200px;height:280px;border:1px solid #ebebeb;"></div>
    <span style="font-family:'Cormorant Garamond',Georgia,serif;
                 font-size:13px;font-style:italic;color:#ccc;letter-spacing:2px;">
      select a product
    </span>
  </div>`}
</div>

<!-- ── EDITORIAL SECTION — z:10, anchored to bottom ──────────────────── -->
<div style="position:absolute;bottom:0;left:0;right:0;z-index:10;padding:0 68px 44px;">

  <div style="height:1.5px;background:#000;margin-bottom:28px;"></div>

  <!-- Headline (left) + Product metadata (right) -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;
              gap:32px;margin-bottom:18px;">

    <div style="flex:1;min-width:0;">
      <div style="font-family:'Cormorant Garamond',Georgia,serif;
                  font-size:${h1Size};font-weight:600;color:#000;
                  line-height:0.86;letter-spacing:-0.5px;text-transform:uppercase;">
        ${h1}
      </div>
      <div style="font-family:'Cormorant Garamond',Georgia,serif;
                  font-size:${h2Size};font-weight:600;color:#000;
                  line-height:0.86;letter-spacing:-0.5px;text-transform:uppercase;
                  margin-top:6px;">
        ${h2}
      </div>
    </div>

    <div style="display:flex;flex-direction:column;align-items:flex-end;
                gap:7px;padding-top:6px;flex-shrink:0;text-align:right;">
      <span style="font-family:'Montserrat',sans-serif;font-size:12px;font-weight:900;
                   letter-spacing:3px;color:#000;text-transform:uppercase;">
        ${pBrand}
      </span>
      ${pCategory ? `
      <span style="font-family:'Roboto Mono',monospace;font-size:9px;color:#aaa;
                   letter-spacing:2px;text-transform:uppercase;">
        ${pCategory}
      </span>` : ''}
      ${pPrice ? `
      <span style="font-family:'Cormorant Garamond',Georgia,serif;font-size:30px;
                   font-weight:600;color:#000;letter-spacing:-0.5px;margin-top:5px;">
        ${pPrice}
      </span>` : ''}
      ${pDiscount ? `
      <span style="font-family:'Roboto Mono',monospace;font-size:9px;color:#000;
                   letter-spacing:2.5px;border:1.5px solid #000;
                   padding:4px 11px;display:inline-block;margin-top:3px;">
        SAVE ${pDiscount}
      </span>` : ''}
    </div>
  </div>

  ${sub ? `
  <div style="font-family:'Cormorant Garamond',Georgia,serif;font-size:16px;
              font-weight:300;font-style:italic;color:#999;
              line-height:1.5;letter-spacing:0.2px;margin-bottom:22px;">
    ${sub}
  </div>` : '<div style="margin-bottom:22px;"></div>'}

  <div style="height:1px;background:#e4e4e4;margin-bottom:14px;"></div>

  <div style="display:flex;justify-content:space-between;align-items:center;">
    <span style="font-family:'Montserrat',sans-serif;font-size:10px;font-weight:900;
                 letter-spacing:7px;color:#000;text-transform:uppercase;">
      ${cta.text}
    </span>
    <span style="font-family:'Roboto Mono',monospace;font-size:9px;color:#ccc;
                 letter-spacing:2px;">
      stockroom.com.au
    </span>
  </div>

</div>

</div>
</body>
</html>`;
}


// ─────────────────────────────────────────────────────────────────────────────
// WINTER EDITORIAL COVER
// Pure typography-driven Instagram carousel cover. No products, no data.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * buildWinterEditorialSchema
 * ─────────────────────────
 * Stockroom-branded Instagram carousel HOOK slide.
 * White background, black typography, Stockroom green accent on arrow + rule.
 * No products, no ecommerce data.
 */
export function buildWinterEditorialSchema({ productCount = 4 } = {}) {
  return {
    template     : 'blank_canvas',
    format       : SCHEMA_FORMATS.MOBILE,
    theme        : SCHEMA_THEMES.WINTER_EDITORIAL,
    background   : { baseColor: '#ffffff', accentColor: '#00E676' },
    headline     : {
      prefix     : 'BUILD A',
      main1      : 'WINTER',
      main2      : 'OUTFIT',
      suffix     : 'WITH ME',
      priceLabel : 'FOR UNDER',
      price      : '$150',
    },
    productCount,
    cta          : { text: 'SEE THE OUTFITS' },
    issue        : { number: "WINTER '26", season: 'SS26' },
    brand        : { name: 'THE STOCKROOM', handle: '@thestockroom' },
    effects      : [SCHEMA_EFFECTS.GRAIN_OVERLAY],
  };
}

// ─── Compiler ────────────────────────────────────────────────────────────────

const _COUNT_WORDS = ['zero','one','two','three','four','five','six','seven','eight','nine','ten'];

function _compileWinterEditorial(schema) {
  const hl     = schema.headline || {};
  const brand  = schema.brand    || { name: 'THE STOCKROOM', handle: '@thestockroom' };
  const issue  = schema.issue    || { number: "WINTER '26", season: 'SS26' };
  const cta    = schema.cta      || { text: 'SEE THE OUTFITS' };
  const accent = schema.background?.accentColor || '#00E676';
  const base   = schema.background?.baseColor   || '#ffffff';
  const count  = Math.max(1, schema.productCount || 4);
  const countWord = (_COUNT_WORDS[count] || String(count)).toUpperCase();
  const ideasWord = count === 1 ? 'idea' : 'ideas';

  const prefix     = (hl.prefix     || 'BUILD A').toUpperCase();
  const main1      = (hl.main1      || 'WINTER').toUpperCase();
  const main2      = (hl.main2      || 'OUTFIT').toUpperCase();
  const suffix     = (hl.suffix     || 'WITH ME').toUpperCase();
  const priceLabel = (hl.priceLabel || 'FOR UNDER').toUpperCase();
  const price      = hl.price || '$150';
  const ctaText    = (cta.text || 'SEE THE OUTFITS').toUpperCase();

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;700;900&family=Roboto+Mono:wght@400;700&display=swap" rel="stylesheet">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  html,body { width:100%; height:100%; overflow:hidden; background:${base}; }
  .root {
    position:relative;
    width:100%;
    height:100%;
    background:${base};
    color:#0c0c0c;
    overflow:hidden;
    font-family:'Montserrat',sans-serif;
  }
  .frame {
    position:absolute;
    inset:34px;
    border:1.5px solid #111;
    z-index:4;
    pointer-events:none;
  }
  .masthead {
    position:absolute;
    top:58px;
    left:72px;
    right:72px;
    z-index:10;
  }
  .masthead-row {
    display:flex;
    align-items:center;
    justify-content:space-between;
    margin-bottom:14px;
  }
  .brand {
    font-size:14px;
    font-weight:900;
    letter-spacing:8px;
    text-transform:uppercase;
  }
  .season {
    font-family:'Roboto Mono',monospace;
    font-size:9px;
    color:#777;
    letter-spacing:3px;
    text-transform:uppercase;
  }
  .masthead-rule {
    height:2px;
    background:linear-gradient(90deg,#111 0%,#111 64%,${accent} 64%,${accent} 100%);
  }
  .headline {
    position:absolute;
    top:174px;
    left:72px;
    right:72px;
    z-index:10;
  }
  .headline-small {
    font-size:38px;
    font-weight:300;
    letter-spacing:0.6px;
    line-height:1;
    margin-bottom:8px;
  }
  .headline-main {
    font-size:112px;
    font-weight:900;
    letter-spacing:-3.5px;
    line-height:0.89;
  }
  .headline-with {
    display:flex;
    align-items:center;
    gap:18px;
    margin-top:14px;
  }
  .headline-with span {
    font-size:38px;
    font-weight:300;
    letter-spacing:0.8px;
    line-height:1;
  }
  .headline-with:after {
    content:'';
    flex:1;
    height:2px;
    background:${accent};
  }
  .edition {
    margin-top:18px;
    font-family:'Roboto Mono',monospace;
    font-size:11px;
    font-weight:400;
    color:#777;
    letter-spacing:3.8px;
    text-transform:uppercase;
  }
  .price-sticker {
    position:absolute;
    top:650px;
    left:72px;
    width:430px;
    height:190px;
    z-index:12;
    background:${accent};
    border:2px solid #111;
    border-radius:28px;
    transform:rotate(-2.2deg);
    box-shadow:12px 14px 0 #111;
    display:flex;
    flex-direction:column;
    justify-content:center;
    padding:24px 32px;
  }
  .price-label {
    font-family:'Roboto Mono',monospace;
    font-size:13px;
    font-weight:700;
    letter-spacing:3.4px;
    text-transform:uppercase;
    margin-bottom:8px;
  }
  .price {
    font-size:98px;
    font-weight:900;
    letter-spacing:-4px;
    line-height:0.82;
  }
  .side-note {
    position:absolute;
    top:676px;
    right:72px;
    width:280px;
    z-index:10;
    font-family:'Roboto Mono',monospace;
    font-size:12px;
    line-height:1.65;
    letter-spacing:1.3px;
    color:#555;
    text-transform:uppercase;
  }
  .side-note strong {
    color:#111;
  }
  .cta-strip {
    position:absolute;
    left:72px;
    right:72px;
    bottom:134px;
    z-index:10;
    min-height:142px;
    border:2px solid #111;
    border-radius:30px;
    background:#111;
    color:#fff;
    display:flex;
    align-items:center;
    justify-content:space-between;
    padding:26px 30px;
  }
  .cta-label {
    font-family:'Roboto Mono',monospace;
    font-size:10px;
    font-weight:700;
    color:${accent};
    letter-spacing:3.4px;
    text-transform:uppercase;
    margin-bottom:9px;
  }
  .cta-main {
    font-size:34px;
    font-weight:900;
    letter-spacing:1px;
    text-transform:uppercase;
    line-height:1;
  }
  .cta-sub {
    font-family:'Roboto Mono',monospace;
    font-size:10px;
    color:#8d8d8d;
    letter-spacing:2px;
    text-transform:uppercase;
    margin-top:12px;
  }
  .arrow {
    flex:0 0 auto;
    width:118px;
    height:78px;
  }
  .footer {
    position:absolute;
    bottom:58px;
    left:72px;
    right:72px;
    z-index:10;
    display:flex;
    justify-content:space-between;
    align-items:center;
    font-family:'Roboto Mono',monospace;
    font-size:9px;
    color:#999;
    letter-spacing:2.4px;
    text-transform:uppercase;
  }
</style>
</head>
<body>
<div class="root">

<!-- ── SVG Layer: soft Stockroom-green background motif ─────────────────── -->
<svg style="position:absolute;inset:0;width:100%;height:100%;z-index:2;pointer-events:none;"
     viewBox="0 0 1080 1350" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="wcGrain" x="0" y="0" width="5" height="5" patternUnits="userSpaceOnUse">
      <circle cx="1.0" cy="1.8" r="0.22" fill="#000" fill-opacity="0.012"/>
      <circle cx="3.8" cy="4.2" r="0.20" fill="#000" fill-opacity="0.009"/>
      <circle cx="2.5" cy="0.5" r="0.16" fill="#000" fill-opacity="0.007"/>
    </pattern>
    <radialGradient id="greenMist" cx="78%" cy="40%" r="42%">
      <stop offset="0%" stop-color="${accent}" stop-opacity="0.18"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
    <pattern id="miniGrid" x="0" y="0" width="26" height="26" patternUnits="userSpaceOnUse">
      <path d="M26 0H0V26" fill="none" stroke="#111" stroke-opacity="0.035" stroke-width="1"/>
    </pattern>
  </defs>
  <rect width="1080" height="1350" fill="url(#greenMist)"/>
  <rect x="544" y="176" width="412" height="482" rx="46"
        fill="${accent}" fill-opacity="0.08" stroke="${accent}" stroke-opacity="0.22" stroke-width="1.5"/>
  <rect x="584" y="216" width="332" height="402" rx="32"
        fill="none" stroke="#111" stroke-opacity="0.055" stroke-width="1"/>
  <rect width="1080" height="1350" fill="url(#miniGrid)"/>
  <rect width="1080" height="1350" fill="url(#wcGrain)"/>
</svg>

<div class="frame"></div>

<div class="masthead">
  <div class="masthead-row">
    <span class="brand">${brand.name}</span>
    <span class="season">${issue.season}</span>
  </div>
  <div class="masthead-rule"></div>
</div>

<div class="headline">
  <div class="headline-small">${prefix}</div>
  <div class="headline-main">${main1}</div>
  <div class="headline-main">${main2}</div>
  <div class="headline-with">
    <span>${suffix}</span>
  </div>
  <div class="edition">(Womens Edition)</div>
</div>

<div class="price-sticker">
  <div class="price-label">${priceLabel}</div>
  <div class="price">${price}</div>
</div>

<div class="side-note">
  <strong>Tap through</strong><br/>
  ${countWord.toLowerCase()} winter-ready outfit ${ideasWord} built around a tight budget.
</div>

<div class="cta-strip">
  <div>
    <div class="cta-label">${ctaText}</div>
    <div class="cta-main">Swipe right</div>
    <div class="cta-sub">1 / ${count} starter look</div>
  </div>
  <svg class="arrow" viewBox="0 0 118 78" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="114" height="74" rx="37" fill="${accent}" stroke="${accent}" stroke-width="4"/>
    <path d="M30 39H78" stroke="#111" stroke-width="8" stroke-linecap="round"/>
    <path d="M68 18L92 39L68 60" fill="none" stroke="#111" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
</div>

<div class="footer">
  <span>${brand.handle || '@thestockroom'}</span>
  <span>${issue.number}</span>
</div>

</div>

<script>
  document.fonts.ready.then(function() {
    document.documentElement.setAttribute('data-fonts-loaded', 'true');
  });
</script>

</body>
</html>`;
}


// ════════════════════════════════════════════════════════════════════════════════
//  TEMPLATE: DEAL SIGNAL
//  Meta / Instagram feed static — price-forward, urgency-led, Reels-adjacent energy
//  Inspired by 2026 fashion performance creatives: overlay math, Shop Now CTA,
//  live-deal framing. Works with a product hero OR as a brand prospecting frame.
// ════════════════════════════════════════════════════════════════════════════════

/**
 * buildDealSignalSchema
 * ─────────────────────
 * High-engagement Blank Canvas ad for Instagram/Facebook feed (4:5 or 1:1).
 * Pulls live savings stats + optional selected product for a “deal is the product” frame.
 */
export function buildDealSignalSchema({
  product = null,
  totalSavings = 0,
  avgSavingsPct = 0,
  dealsLive = 0,
  format = SCHEMA_FORMATS.MOBILE,
} = {}) {
  const avg = Math.max(0, Math.round(Number(avgSavingsPct) || 0));
  const deals = Math.max(0, Number(dealsLive) || 0);
  const saved = Math.max(0, Math.round(Number(totalSavings) || 0));

  const price = Number(product?.price) || 0;
  const original = Number(product?.originalPrice) || 0;
  const discount = product?.discountPercentage > 0
    ? Math.round(product.discountPercentage)
    : (original > price && original > 0
      ? Math.round(((original - price) / original) * 100)
      : avg);

  const image = product?.image
    || (Array.isArray(product?.images) && product.images[0])
    || '';

  const formatPrice = (n) => (n > 0 ? `$${Number(n).toFixed(n % 1 ? 2 : 0)}` : null);

  return {
    template: 'blank_canvas',
    format,
    theme: SCHEMA_THEMES.DEAL_SIGNAL,

    background: {
      baseColor: '#070707',
      accentColor: '#00E676',
    },

    product: product ? {
      name: product.name || 'Featured Deal',
      brand: product.brand || product.merchantName || 'Stockroom',
      merchant: product.merchantName || '',
      image,
      price: formatPrice(price),
      originalPrice: formatPrice(original > price ? original : 0),
      discount,
    } : null,

    headline: {
      eyebrow: 'LIVE ON STOCKROOM',
      title: product ? 'THIS DEAL IS LIVE' : 'THE MARKET JUST MOVED',
      support: product
        ? 'Premium piece. Unexpected price. Tap before it flips.'
        : 'AU fashion discounts, tracked in one feed — not another full-price scroll.',
    },

    metrics: {
      avgDiscount: avg,
      dealsLive: deals,
      marketSavings: saved >= 1000 ? `$${Math.round(saved / 1000)}K` : `$${saved}`,
    },

    cta: { text: product ? 'GET THE DEAL' : 'OPEN THE FEED' },

    brand: {
      name: 'STOCKROOM',
      handle: '@thestockroom',
      tag: 'AU DISCOUNTED MARKET FEED',
    },

    effects: [
      SCHEMA_EFFECTS.GRAIN_OVERLAY,
      SCHEMA_EFFECTS.RADIAL_GLOW,
    ],
  };
}

function _escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function _compileDealSignal(schema) {
  const accent = schema.background?.accentColor || '#00E676';
  const base = schema.background?.baseColor || '#070707';
  const brand = schema.brand || { name: 'STOCKROOM', handle: '@thestockroom', tag: 'AU DISCOUNTED MARKET FEED' };
  const hl = schema.headline || {};
  const metrics = schema.metrics || {};
  const cta = (schema.cta?.text || 'GET THE DEAL').toUpperCase();
  const product = schema.product;
  const hasProduct = Boolean(product?.image || product?.name);

  const avg = metrics.avgDiscount || 0;
  const deals = Number(metrics.dealsLive || 0).toLocaleString();
  const saved = metrics.marketSavings || '$0';
  const discount = hasProduct ? (product.discount || avg) : avg;
  const discountLabel = discount > 0 ? `${discount}%` : `${Math.max(avg, 40)}%`;

  const productName = _escapeHtml(
    String(product?.name || '')
      .replace(/\b[A-Z0-9]{10,}\b/g, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
  const productBrand = _escapeHtml(product?.brand || '');
  const productMerchant = _escapeHtml(product?.merchant || '');
  const priceNow = _escapeHtml(product?.price || '');
  const priceWas = _escapeHtml(product?.originalPrice || '');
  const imageUrl = _escapeHtml(product?.image || '');

  const heroBlock = hasProduct && imageUrl
    ? `
      <div class="hero-media">
        <img src="${imageUrl}" alt="" />
        <div class="hero-scrim"></div>
      </div>`
    : `
      <div class="hero-abstract" aria-hidden="true">
        <div class="orbit orbit-a"></div>
        <div class="orbit orbit-b"></div>
        <div class="signal-bars">
          <span></span><span></span><span></span><span></span><span></span>
        </div>
        <div class="ghost-mark">Stock<span>room</span></div>
      </div>`;

  const productMeta = hasProduct
    ? `
      <div class="product-meta">
        <div class="product-brand">${productBrand}${productMerchant && productMerchant !== productBrand ? ` · ${productMerchant}` : ''}</div>
        <h2 class="product-name">${productName || 'Featured Deal'}</h2>
        <div class="price-row">
          ${priceNow ? `<span class="price-now">${priceNow}</span>` : ''}
          ${priceWas ? `<span class="price-was">${priceWas}</span>` : ''}
          ${discount > 0 ? `<span class="price-save">SAVE ${discount}%</span>` : ''}
        </div>
      </div>`
    : `
      <div class="product-meta brand-mode">
        <div class="product-brand">AU STREETWEAR · DESIGNER · EVERYDAY</div>
        <h2 class="product-name">Premium brands.<br/>Unexpected prices.</h2>
        <div class="price-row">
          <span class="price-now">UP TO ${discountLabel} OFF</span>
          <span class="price-save">${deals} DEALS LIVE</span>
        </div>
      </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Syne:wght@700;800&family=IBM+Plex+Mono:wght@400;600&display=swap" rel="stylesheet" />
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: ${base};
  }
  .root {
    position: relative;
    width: 100%;
    height: 100%;
    background: ${base};
    color: #fff;
    font-family: 'Syne', sans-serif;
    overflow: hidden;
  }

  .hero-media, .hero-abstract {
    position: absolute;
    inset: 0;
    z-index: 0;
  }
  .hero-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
    transform: scale(1.04);
    animation: drift 8s ease-in-out infinite alternate;
  }
  .hero-scrim {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(7,7,7,0.55) 0%, rgba(7,7,7,0.15) 32%, rgba(7,7,7,0.25) 55%, rgba(7,7,7,0.92) 78%, #070707 100%),
      radial-gradient(ellipse at 70% 20%, rgba(0,230,118,0.18) 0%, transparent 45%);
  }

  .hero-abstract {
    background:
      radial-gradient(ellipse at 30% 20%, rgba(0,230,118,0.22) 0%, transparent 42%),
      radial-gradient(ellipse at 80% 70%, rgba(255,255,255,0.05) 0%, transparent 40%),
      linear-gradient(160deg, #0b0b0b 0%, #050505 55%, #0a1210 100%);
  }
  .orbit {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(0,230,118,0.28);
    animation: pulseOrbit 5s ease-in-out infinite;
  }
  .orbit-a { width: 62vmin; height: 62vmin; top: 12%; left: 50%; transform: translateX(-50%); }
  .orbit-b {
    width: 38vmin; height: 38vmin; top: 22%; left: 50%; transform: translateX(-50%);
    border-color: rgba(255,255,255,0.12);
    animation-delay: 0.8s;
  }
  .signal-bars {
    position: absolute;
    left: 10%;
    bottom: 38%;
    display: flex;
    align-items: flex-end;
    gap: 10px;
    height: 18vmin;
    opacity: 0.85;
  }
  .signal-bars span {
    display: block;
    width: 1.1vmin;
    background: ${accent};
    border-radius: 2px;
    animation: barPulse 1.6s ease-in-out infinite;
  }
  .signal-bars span:nth-child(1) { height: 28%; animation-delay: 0s; }
  .signal-bars span:nth-child(2) { height: 55%; animation-delay: 0.15s; }
  .signal-bars span:nth-child(3) { height: 100%; animation-delay: 0.3s; }
  .signal-bars span:nth-child(4) { height: 42%; animation-delay: 0.45s; }
  .signal-bars span:nth-child(5) { height: 70%; animation-delay: 0.6s; }
  .ghost-mark {
    position: absolute;
    right: 8%;
    top: 34%;
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(48px, 14vmin, 120px);
    letter-spacing: 0.02em;
    line-height: 0.85;
    color: rgba(255,255,255,0.06);
    text-align: right;
    transform: rotate(-8deg);
  }
  .ghost-mark span { color: rgba(0,230,118,0.14); }

  .chrome {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: clamp(22px, 4.5%, 42px);
  }

  .top-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }
  .live-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid rgba(0,230,118,0.55);
    background: rgba(0,0,0,0.45);
    backdrop-filter: blur(8px);
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(9px, 1.7vmin, 12px);
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${accent};
  }
  .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 0 rgba(0,230,118,0.7);
    animation: livePulse 1.6s ease-out infinite;
  }
  .discount-badge {
    min-width: clamp(84px, 18vmin, 128px);
    padding: 14px 16px 12px;
    background: ${accent};
    color: #04140a;
    text-align: center;
    transform: rotate(3deg);
    box-shadow: 0 12px 40px rgba(0,0,0,0.35);
    animation: badgeIn 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }
  .discount-badge .off {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(9px, 1.6vmin, 11px);
    font-weight: 600;
    letter-spacing: 0.18em;
  }
  .discount-badge .pct {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 11vmin, 78px);
    line-height: 0.85;
    letter-spacing: 0.02em;
  }

  .mid-copy {
    margin-top: auto;
    max-width: 92%;
  }
  .eyebrow {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(9px, 1.7vmin, 12px);
    letter-spacing: 0.22em;
    color: rgba(255,255,255,0.55);
    margin-bottom: 10px;
    text-transform: uppercase;
  }
  .title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 11.5vmin, 92px);
    line-height: 0.9;
    letter-spacing: 0.01em;
    margin-bottom: 12px;
    text-wrap: balance;
  }
  .support {
    font-size: clamp(13px, 2.4vmin, 18px);
    font-weight: 700;
    line-height: 1.35;
    color: rgba(255,255,255,0.78);
    max-width: 28ch;
  }

  .bottom-stack {
    display: flex;
    flex-direction: column;
    gap: clamp(14px, 2.5vmin, 22px);
  }

  .product-meta .product-brand {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(9px, 1.6vmin, 11px);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${accent};
    margin-bottom: 6px;
  }
  .product-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(28px, 6.5vmin, 48px);
    line-height: 0.95;
    letter-spacing: 0.01em;
    margin-bottom: 10px;
    max-width: 18ch;
  }
  .brand-mode .product-name { max-width: 14ch; }
  .price-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 10px 14px;
  }
  .price-now {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(28px, 6vmin, 44px);
    color: #fff;
    letter-spacing: 0.02em;
  }
  .price-was {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(12px, 2.2vmin, 16px);
    color: rgba(255,255,255,0.4);
    text-decoration: line-through;
  }
  .price-save {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(10px, 1.8vmin, 12px);
    font-weight: 600;
    letter-spacing: 0.12em;
    color: #04140a;
    background: ${accent};
    padding: 5px 8px;
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    border-top: 1px solid rgba(255,255,255,0.12);
    padding-top: 12px;
  }
  .stat {
    min-width: 0;
  }
  .stat-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(8px, 1.4vmin, 10px);
    letter-spacing: 0.14em;
    color: rgba(255,255,255,0.4);
    margin-bottom: 4px;
    text-transform: uppercase;
  }
  .stat-value {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(20px, 4.2vmin, 30px);
    letter-spacing: 0.02em;
    color: #fff;
  }

  .cta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .cta {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    background: #fff;
    color: #070707;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(12px, 2.3vmin, 16px);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    animation: ctaGlow 2.8s ease-in-out infinite;
  }
  .cta-arrow {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${accent};
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .cta-arrow svg { width: 14px; height: 14px; }
  .handle {
    font-family: 'IBM Plex Mono', monospace;
    font-size: clamp(9px, 1.5vmin, 11px);
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.45);
    white-space: nowrap;
  }

  .grain {
    position: absolute;
    inset: 0;
    z-index: 3;
    pointer-events: none;
    opacity: 0.09;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    mix-blend-mode: overlay;
  }

  @keyframes livePulse {
    0% { box-shadow: 0 0 0 0 rgba(0,230,118,0.65); }
    70% { box-shadow: 0 0 0 10px rgba(0,230,118,0); }
    100% { box-shadow: 0 0 0 0 rgba(0,230,118,0); }
  }
  @keyframes pulseOrbit {
    0%, 100% { opacity: 0.45; transform: translateX(-50%) scale(1); }
    50% { opacity: 0.9; transform: translateX(-50%) scale(1.03); }
  }
  @keyframes barPulse {
    0%, 100% { opacity: 0.35; transform: scaleY(0.7); }
    50% { opacity: 1; transform: scaleY(1); }
  }
  @keyframes drift {
    from { transform: scale(1.04) translate3d(0,0,0); }
    to { transform: scale(1.08) translate3d(0,-1.5%,0); }
  }
  @keyframes badgeIn {
    from { opacity: 0; transform: rotate(3deg) translateY(-12px) scale(0.92); }
    to { opacity: 1; transform: rotate(3deg) translateY(0) scale(1); }
  }
  @keyframes ctaGlow {
    0%, 100% { box-shadow: 0 0 0 rgba(0,230,118,0); }
    50% { box-shadow: 0 0 28px rgba(0,230,118,0.25); }
  }
</style>
</head>
<body>
  <main class="root" role="img" aria-label="Stockroom deal advertisement">
    ${heroBlock}
    <div class="chrome">
      <div class="top-row">
        <div class="live-pill"><span class="live-dot"></span>${_escapeHtml(hl.eyebrow || 'LIVE ON STOCKROOM')}</div>
        <div class="discount-badge">
          <div class="off">UP TO</div>
          <div class="pct">${_escapeHtml(discountLabel)}</div>
          <div class="off">OFF</div>
        </div>
      </div>

      <div class="mid-copy">
        <div class="eyebrow">${_escapeHtml(brand.tag || 'AU DISCOUNTED MARKET FEED')}</div>
        <h1 class="title">${_escapeHtml(hl.title || 'THE MARKET JUST MOVED')}</h1>
        <p class="support">${_escapeHtml(hl.support || '')}</p>
      </div>

      <div class="bottom-stack">
        ${productMeta}
        <div class="stats">
          <div class="stat">
            <div class="stat-label">Avg Off</div>
            <div class="stat-value">${avg}%</div>
          </div>
          <div class="stat">
            <div class="stat-label">Deals Live</div>
            <div class="stat-value">${deals}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Tracked</div>
            <div class="stat-value">${_escapeHtml(saved)}</div>
          </div>
        </div>
        <div class="cta-row">
          <div class="cta">
            <span>${_escapeHtml(cta)}</span>
            <span class="cta-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="#04140a" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter">
                <path d="M5 12h12"/><path d="M13 6l6 6-6 6"/>
              </svg>
            </span>
          </div>
          <div class="handle">${_escapeHtml(brand.handle || '@thestockroom')}</div>
        </div>
      </div>
    </div>
    <div class="grain"></div>
  </main>
</body>
</html>`;
}


// ─── Main compiler — dispatches by theme ─────────────────────────────────────

/**
 * compileCreativeSchemaToHTML
 * ───────────────────────────
 * Converts any CreativeSchema into a fully self-contained HTML string
 * ready for iframe srcDoc injection and RenderEngine export.
 *
 * @param {object} schema
 * @returns {string}
 */
export function compileCreativeSchemaToHTML(schema) {
  switch (schema?.theme) {
    case SCHEMA_THEMES.WINTER_EDITORIAL:
      return _compileWinterEditorial(schema);
    case SCHEMA_THEMES.MAGAZINE_COVER:
      return _compileMagazineCover(schema);
    case SCHEMA_THEMES.MARKET_INTELLIGENCE:
      return _compileMarketIntelligence(schema);
    case SCHEMA_THEMES.STREETWEAR_INTEL:
      return _compileStreetswearIntel(schema);
    case SCHEMA_THEMES.DEAL_SIGNAL:
      return _compileDealSignal(schema);
    default:
      return _compileDealSignal(schema);
  }
}
